/**
 * A cleaner: a contact who works for Majik, with their shift and whether they
 * get texts (docs/majik/DATA-ARCHITECTURE-SPEC.md → `cleaners`); and the shape
 * of their regular week — which location they clean and for how many hours
 * each weekday — which the recurring jobs decide (D-034). Pure.
 */
import { z } from 'zod'
import { recordIdSchema } from '../shared/record-id.ts'

export const cleanerStatuses = ['active', 'inactive'] as const
export const shifts = ['daytime', 'nighttime'] as const
export type Shift = (typeof shifts)[number]

export const shiftLabels: Record<Shift, string> = { daytime: 'Day', nighttime: 'Night' }

/** What a cleaner with no shift says (an imported one Monday gave no Day Type, D-037). */
export const NO_SHIFT = 'Shift not set'

/** "Daytime", "Nighttime", or "Shift not set". */
export function shiftText(shift: Shift | null): string {
  return shift ? `${shiftLabels[shift]}time` : NO_SHIFT
}

export const cleanerSchema = z.object({
  id: recordIdSchema,
  contactId: recordIdSchema,
  status: z.enum(cleanerStatuses),
  /** Null only on an imported cleaner Monday gave no Day Type; the form still asks (D-037). */
  shift: z.enum(shifts).nullable().default(null),
  /** Gets the nightly list and schedules by text, as well as email. */
  notifyByText: z.boolean().default(true),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().default(null),
})
export type Cleaner = z.infer<typeof cleanerSchema>

const hours = z.number().min(0).max(24).multipleOf(0.25)

export const weekHoursSchema = z.object({
  mon: hours,
  tue: hours,
  wed: hours,
  thu: hours,
  fri: hours,
  sat: hours,
  sun: hours,
})
export type WeekHours = z.infer<typeof weekHoursSchema>

/**
 * Who cleans a location, how many hours each weekday and from when — worked
 * out from the recurring jobs on every read and stored nowhere (D-034,
 * `weeklyServiceOf` in src/core/jobs). `cleanerId` is null for the hours of a
 * recurring job nobody is on yet.
 */
export interface Assignment {
  cleanerId: string | null
  locationId: string
  hours: WeekHours
  /** Their earliest start here in the week, 'HH:mm'. */
  startTime: string | null
}

export function weeklyHours(week: WeekHours): number {
  return week.mon + week.tue + week.wed + week.thu + week.fri + week.sat + week.sun
}

/** "2.5 h", "6 h", "3.75 h". */
export function hoursText(value: number): string {
  return `${Number.isInteger(value) ? value : Number(value.toFixed(2))} h`
}
